<template>
	<DatePicker
		:model-value="modelDate"
		:min-date="minDate"
		:max-date="maxDate"
		:masks="{ input: 'WWWW, D MMMM YYYY' }"
		:popover="{ visibility: disabled || readonly ? 'hidden' : 'click' }"
		:update-on-input="false"
		color="orange"
		:is-dark="CALENDAR_DARK_MODE"
		title-position="center"
		@update:model-value="onDateSelected">
		<template #default="{ inputValue, inputEvents }">
			<div class="b-form-datepicker input-group" :class="{ 'is-disabled': disabled }">
				<div class="input-group-prepend">
					<span class="input-group-text">
						<i class="fa fa-calendar"></i>
					</span>
				</div>
				<input
					:id="id"
					type="text"
					readonly
					:class="['form-control', validityClass]"
					:disabled="disabled"
					:placeholder="placeholder || 'No date selected'"
					:value="inputValue"
					v-on="inputEvents" />
			</div>
		</template>
		<template v-if="todayButton || resetButton || closeButton" #footer>
			<div class="b-form-datepicker-footer">
				<button v-if="todayButton" type="button"
					class="btn btn-sm btn-outline-secondary" @click="selectToday">
					Today
				</button>
				<button v-if="resetButton" type="button"
					class="btn btn-sm btn-outline-secondary" @click="clearDate">
					Clear
				</button>
				<button v-if="closeButton" type="button"
					class="btn btn-sm btn-outline-secondary" @click="closePopover">
					Close
				</button>
			</div>
		</template>
	</DatePicker>
</template>

<script setup>
	import { computed, onErrorCaptured } from 'vue';
	import { DatePicker } from 'v-calendar';
	import 'v-calendar/style.css';
	import { CALENDAR_DARK_MODE } from '@js/utilities/calendar-dark-mode.js';

	const props = defineProps({
		modelValue: { type: [String, Date, null], default: null },
		value: { type: [String, Date, null], default: null },
		id: { type: String, default: null },
		disabled: { type: Boolean, default: false },
		readonly: { type: Boolean, default: false },
		placeholder: { type: String, default: null },
		min: { type: [String, Date, null], default: null },
		max: { type: [String, Date, null], default: null },
		state: { type: [Boolean, null], default: null },
		todayButton: { type: Boolean, default: false },
		resetButton: { type: Boolean, default: false },
		closeButton: { type: Boolean, default: false },
	});

	const emit = defineEmits(['update:modelValue', 'change', 'input']);

	const toDate = (v) => {
		if (v == null || v === '') return null;
		if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
		if (typeof v === 'string') {
			const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
			if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
			const parsed = new Date(v);
			return Number.isNaN(parsed.getTime()) ? null : parsed;
		}
		return null;
	};

	const toIso = (d) => {
		if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
		const y = d.getFullYear();
		const m = String(d.getMonth() + 1).padStart(2, '0');
		const day = String(d.getDate()).padStart(2, '0');
		return `${y}-${m}-${day}`;
	};

	const modelDate = computed(() => {
		const raw = props.modelValue !== undefined && props.modelValue !== null ? props.modelValue : props.value;
		return toDate(raw);
	});

	// explicit emit handler, fired by v-calendar when the user picks a date.
	const onDateSelected = (newDate) => {
		const iso = toIso(newDate);
		emit('update:modelValue', iso);
		emit('input', iso);
		emit('change', iso);
	};

	const selectToday = () => {
		onDateSelected(new Date());
	};

	const clearDate = () => {
		emit('update:modelValue', null);
		emit('input', null);
		emit('change', null);
	};

	const closePopover = () => {
		// v-calendar's popover has no imperative close method; a click outside
		// the popover is what it listens for to dismiss itself.
		document.body.click();
	};

	const minDate = computed(() => toDate(props.min));
	const maxDate = computed(() => toDate(props.max));

	const validityClass = computed(() => {
		if (props.state === true) return 'is-valid';
		if (props.state === false) return 'is-invalid';
		return '';
	});

	onErrorCaptured((err) => {
		// match the v-calendar v3 hover-render TypeError specifically
		const msg = err?.message || '';
		const stack = err?.stack || '';
		const isVCalendarHoverBug =
			msg.includes('\'dayIndex\'') || (msg.includes('dayIndex') && stack.includes('DateRangeContext'));

		if (isVCalendarHoverBug) {
			// eslint-disable-next-line no-console
			console.debug('[BFormDatepicker] swallowed known v-calendar hover-render bug');
			return false;
		}
		// any other error
		return undefined;
	});
</script>

<style>
.b-form-datepicker .form-control[readonly] {
	background-color: #fff;
	cursor: pointer;
}
.b-form-datepicker .input-group-text {
	background-color: #fff;
}

.vc-popover-content-wrapper .vc-container {
	border: 1px solid #ced4da;
	border-radius: 0.25rem;
	font-family: inherit;
}
.vc-container .vc-header {
	padding: 10px 12px;
}
.vc-container .vc-title {
	font-size: 1rem;
	font-weight: 600;
	color: #212529;
}
.vc-container .vc-weekday {
	color: #6c757d;
	font-weight: 600;
}

/* selected value highlight */
.vc-container .vc-highlight {
	background-color: #fd7e14 !important;
}
.vc-container .vc-day-content:hover {
	background-color: #fff3e0;
}
.vc-container .vc-arrow {
	color: #495057;
}
.b-form-datepicker-footer {
	display: flex;
	justify-content: space-between;
	gap: 0.5rem;
	padding: 0.5rem;
	border-top: 1px solid #ced4da;
}
.b-form-datepicker.is-disabled .form-control,
.b-form-datepicker.is-disabled .input-group-text {
	background-color: #e9ecef;
}
</style>
